import React, { useState, useEffect } from 'react';
import SchoolNavigation from './management/SchoolNavigation';
import SchoolWorkflows from './management/SchoolWorkflows';
import ManagementPanel from './management/ManagementPanel';
import { useAuth } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Textarea } from './ui/textarea';
import StudentDetailsDialog from './StudentDetailsDialog';
import TeacherDetailsDialog from './TeacherDetailsDialog';
import HomeroomSelect from './HomeroomSelect';
import SettingsTab from './SettingsTab';
import GradeReportsTab from './GradeReportsTab';
import HumanResourcesTab from './HumanResourcesTab';
import ClockInOutTab from './ClockInOutTab';
import ClockHistoryTab from './ClockHistoryTab';
import MyProfileTab from './MyProfileTab';
import WeeklyPlanningTab from './WeeklyPlanningTab';
import PayrollTab from './PayrollTab';
import InvoicesTab from './InvoicesTab';
import CalendarsTab from './CalendarsTab';
import PackagesTab from './admin/PackagesTab';
import HistoricalGradesTab from './admin/HistoricalGradesTab';
import AcademicProgressTab from './admin/AcademicProgressTab';
import AdminAttendanceTab from './admin/AdminAttendanceTab';
import DashboardOverview from './admin/DashboardOverview';
import FormsTab from './admin/FormsTab';
import CashFlowTab from './admin/CashFlowTab';
import LeaveRequestsTab from './LeaveRequestsTab';
import { 
  Users, 
  GraduationCap, 
  BarChart3, 
  Calendar, 
  LogOut,
  Home,
  BookOpen,
  School,
  Plus,
  Edit,
  Eye,
  Trash2,
  Clock,
  RefreshCw,
  UserPlus,
  Mail,
  Phone,
  AlertTriangle,
  Menu,
  X,
  Settings,
  FileText,
  DollarSign,
  TrendingUp,
  ClipboardCheck,
  CreditCard,
  Bell,
  Send,
  Download,
  Briefcase,
  Timer,
  UserCircle,
  Calculator,
  Receipt,
  Wallet,
  CalendarDays,
  Search
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { formatLastLogin, getLastLoginColor } from '../utils/lastLogin';

// Helper function to handle validation errors
const handleValidationError = (error) => {
  if (error.response?.data?.detail) {
    if (Array.isArray(error.response.data.detail)) {
      // Handle Pydantic validation errors
      const messages = error.response.data.detail.map(err => `${err.loc?.join('.')} - ${err.msg}`).join('\n');
      return messages;
    } else {
      return error.response.data.detail;
    }
  }
  return 'Error desconocido';
};

const AdminDashboard = () => {
  const { logout, user, schoolInfo } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Permission helpers based on admin sub-role
  const subrole = user?.admin_subrole || 'total';
  const canSeeAcademic = subrole === 'total' || subrole === 'secretary';   // students, teachers, subjects, reports, planning, forms, historical, calendars, announcements
  const canSeeFinance = subrole === 'total' || subrole === 'accounting';   // payroll, hr, invoices, cashflow, packages, income, clock
  const canSeeConfig = subrole === 'total';                                // settings, packages-mgmt, admin-users

  // Dashboard stats
  const [dashboardStats, setDashboardStats] = useState({
    totalStudents: 0,
    totalTeachers: 0,
    totalSubjects: 0,
    activeAcademicYear: null
  });

  // Data states
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [parentActivity, setParentActivity] = useState({}); // student_id -> { mother, father }
  const [subjects, setSubjects] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [showWithdrawn, setShowWithdrawn] = useState(false);
  const [withdrawnStudents, setWithdrawnStudents] = useState([]);
  const [studentSearch, setStudentSearch] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [incomeStats, setIncomeStats] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [announcements, setAnnouncements] = useState([]);
  const [showAnnouncementDialog, setShowAnnouncementDialog] = useState(false);
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    content: '',
    attachment_url: '',
    attachment_name: '',
    attachment_mention: '',
    target_grades: []
  });

  // Dialog states
  const [showStudentDialog, setShowStudentDialog] = useState(false);
  const [showTeacherDialog, setShowTeacherDialog] = useState(false);
  const [showSubjectDialog, setShowSubjectDialog] = useState(false);
  const [showAcademicYearDialog, setShowAcademicYearDialog] = useState(false);
  const [showStudentDetails, setShowStudentDetails] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showTeacherDetails, setShowTeacherDetails] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [selectedStudentForBlock, setSelectedStudentForBlock] = useState('');
  const [blockReason, setBlockReason] = useState('');

  // Settings state
  const [settings, setSettings] = useState({});
  const [settingsLoading, setSettingsLoading] = useState(false);

  // Student management state
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Form states
  const [studentForm, setStudentForm] = useState({
    first_name: '',
    middle_name: '',
    last_name_paternal: '',
    last_name_maternal: '',
    birth_date: '',
    ssn_last_4: '',
    father_full_name: '',
    mother_full_name: '',
    phone_number: '',
    residential_address: '',
    postal_address: '',
    email: '',
    assigned_grade: '',
    medical_conditions: [],
    emergency_contact_name: '',
    emergency_contact_phone: '',
    monthly_tuition: 0,
    create_parent_account: true,
    create_father_account: false,
    mother_email: '',
    father_email: '',
    parent_email: '',
    primary_parent_role: 'mother',
    secondary_parent_role: 'father'
  });

  // Editing states
  const [editingStudent, setEditingStudent] = useState(null);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [editingSubject, setEditingSubject] = useState(null);

  const [teacherForm, setTeacherForm] = useState({
    full_name: '',
    homeroom_grade: null,
    assigned_grades: [],
    subjects: [],
    phone: '',
    address: '',
    username: '',
    email: '',
    password: ''
  });

  const [subjectForm, setSubjectForm] = useState({
    name: '',
    code: '',
    description: '',
    credits: '',
    is_subclass: false,
    assigned_grades: [],
    assigned_teacher_id: ''
  });

  const [academicYearForm, setAcademicYearForm] = useState({
    name: '',
    start_date: '',
    end_date: '',
    is_current: false
  });

  // Fetch functions
  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get('/dashboard/stats');
      const data = response.data;
      setDashboardStats({
        totalStudents: data.total_students || 0,
        totalTeachers: data.total_teachers || 0,
        totalSubjects: data.total_subjects || 0,
        activeAcademicYear: data.active_academic_year || null
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await axios.get('/students');
      setStudents(response.data);
      // Fetch parent activity in parallel (best-effort, don't fail UI on error)
      try {
        const act = await axios.get('/students/parents-activity');
        const map = {};
        (act.data || []).forEach(row => { map[row.student_id] = row; });
        setParentActivity(map);
      } catch (e) { /* silent */ }
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error('Error al cargar estudiantes');
    }
  };

  const fetchTeachers = async () => {
    try {
      const response = await axios.get('/teachers');
      setTeachers(response.data);
    } catch (error) {
      console.error('Error fetching teachers:', error);
      toast.error('Error al cargar profesores');
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await axios.get('/subjects');
      setSubjects(response.data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
      toast.error('Error al cargar materias');
    }
  };

  const fetchAcademicYears = async () => {
    try {
      const response = await axios.get('/academic-years');
      setAcademicYears(response.data);
    } catch (error) {
      console.error('Error fetching academic years:', error);
      toast.error('Error al cargar años académicos');
    }
  };

  const fetchBlocks = async () => {
    try {
      const response = await axios.get('/blocks');
      setBlocks(response.data);
    } catch (error) {
      console.error('Error fetching blocks:', error);
      toast.error('Error al cargar bloqueos');
    }
  };

  const fetchIncomeStats = async () => {
    try {
      const response = await axios.get('/payments/stats/monthly', {
        params: {
          year: selectedYear,
          month: selectedMonth
        }
      });
      setIncomeStats(response.data);
    } catch (error) {
      console.error('Error fetching income stats:', error);
      toast.error('Error al cargar estadísticas de ingresos');
    }
  };

  const exportPaymentsToExcel = () => {
    if (!incomeStats || !incomeStats.payments || incomeStats.payments.length === 0) {
      toast.error('No hay pagos para exportar');
      return;
    }

    // Preparar datos para el Excel
    const excelData = incomeStats.payments.map(payment => ({
      'Fecha': new Date(payment.paid_date).toLocaleDateString('es-MX'),
      'ID Estudiante': payment.student_number || 'N/A',
      'Nombre Estudiante': payment.student_name || 'Desconocido',
      'Tipo de Pago': payment.payment_type === 'tuition' ? 'Colegiatura' : 
                      payment.payment_type === 'enrollment' ? 'Matrícula' : 
                      payment.payment_type === 'books' ? 'Libros' : 
                      payment.payment_type === 'supplies' ? 'Materiales' :
                      payment.payment_type === 'uniform' ? 'Uniforme' :
                      payment.payment_type === 'transport' ? 'Transporte' :
                      payment.payment_type === 'food' ? 'Alimentación' :
                      payment.payment_type === 'activities' ? 'Actividades' :
                      payment.payment_type,
      'Monto': payment.amount,
      'Método': payment.payment_method || 'N/A',
      'Descripción': payment.description || 'Sin descripción'
    }));

    // Crear libro de Excel
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Pagos');

    // Ajustar ancho de columnas
    const columnWidths = [
      { wch: 12 }, // Fecha
      { wch: 15 }, // ID Estudiante
      { wch: 30 }, // Nombre Estudiante
      { wch: 15 }, // Tipo de Pago
      { wch: 12 }, // Monto
      { wch: 12 }, // Método
      { wch: 40 }  // Descripción
    ];
    worksheet['!cols'] = columnWidths;

    // Generar nombre de archivo
    const monthName = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                       'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'][selectedMonth - 1];
    const fileName = `Reporte_Pagos_${monthName}_${selectedYear}.xlsx`;

    // Descargar archivo
    XLSX.writeFile(workbook, fileName);
    toast.success(`Reporte exportado: ${fileName}`);
  };




  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get('/announcements');
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Error fetching announcements:', error);
      toast.error('Error al cargar anuncios');
    }
  };

  const handleAnnouncementSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post('/announcements', announcementForm);
      const data = response.data;
      
      toast.success(
        `Anuncio enviado a ${data.emails_sent} de ${data.total_recipients} padres`,
        { duration: 5000 }
      );
      
      setShowAnnouncementDialog(false);
      setAnnouncementForm({
        title: '',
        content: '',
        attachment_url: '',
        attachment_name: '',
        attachment_mention: '',
        target_grades: []
      });
      fetchAnnouncements();
    } catch (error) {
      console.error('Error creating announcement:', error);
      toast.error(handleValidationError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAnnouncement = async (announcementId) => {
    if (!window.confirm('¿Estás seguro de eliminar este anuncio?')) return;
    
    try {
      await axios.delete(`/announcements/${announcementId}`);
      toast.success('Anuncio eliminado exitosamente');
      fetchAnnouncements();
    } catch (error) {
      console.error('Error deleting announcement:', error);
      toast.error(handleValidationError(error));
    }
  };

  const handleBlockStudent = async () => {
    if (!selectedStudentForBlock || !blockReason.trim()) {
      toast.error('Por favor complete todos los campos');
      return;
    }

    setLoading(true);
    try {
      await axios.post('/blocks', {
        student_id: selectedStudentForBlock,
        reason: blockReason
      });
      
      toast.success('Estudiante bloqueado exitosamente');
      setShowBlockDialog(false);
      setSelectedStudentForBlock('');
      setBlockReason('');
      fetchBlocks();
      fetchStudents();
    } catch (error) {
      console.error('Error blocking student:', error);
      toast.error('Error al bloquear estudiante');
    } finally {
      setLoading(false);
    }
  };

  const handleUnblockStudent = async (blockId) => {
    if (!window.confirm('¿Desbloquear este estudiante?')) return;

    try {
      await axios.put(`/blocks/${blockId}/deactivate`);
      toast.success('Estudiante desbloqueado exitosamente');
      fetchBlocks();
      fetchStudents();
    } catch (error) {
      console.error('Error unblocking student:', error);
      toast.error('Error al desbloquear estudiante');
    }
  };

  // Settings functions
  const fetchSettings = async () => {
    try {
      setSettingsLoading(true);
      const response = await axios.get('/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Error al cargar configuraciones');
    } finally {
      setSettingsLoading(false);
    }
  };

  const updateSettings = async (updatedSettings) => {
    try {
      setSettingsLoading(true);
      await axios.put('/settings', updatedSettings);
      toast.success('Configuraciones actualizadas exitosamente');
      await fetchSettings();
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error(handleValidationError(error));
    } finally {
      setSettingsLoading(false);
    }
  };

  const promoteStudents = async () => {
    if (!window.confirm('¿Estás seguro de promover todos los estudiantes activos al siguiente grado? Esta acción no se puede deshacer.')) {
      return;
    }
    
    try {
      const response = await axios.post('/students/promote');
      const data = response.data;
      toast.success(
        `Promoción completada: ${data.promoted} estudiantes promovidos, ${data.graduated} graduados`,
        { duration: 5000 }
      );
      
      if (data.errors && data.errors.length > 0) {
        toast.warning(`Se encontraron ${data.errors.length} errores durante la promoción`);
        console.error('Promotion errors:', data.errors);
      }
      
      fetchStudents();
    } catch (error) {
      console.error('Error promoting students:', error);
      toast.error(handleValidationError(error));
    }
  };

  // Student management functions
  const handleSelectStudent = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(students.map(student => student.id));
    }
    setSelectAll(!selectAll);
  };

  const filteredStudents = studentSearch.trim()
    ? students.filter(s => {
        const term = studentSearch.toLowerCase();
        const fullName = `${s.first_name || ''} ${s.middle_name || ''} ${s.last_name_paternal || ''} ${s.last_name_maternal || ''}`.toLowerCase();
        return fullName.includes(term) || (s.student_number || '').toLowerCase().includes(term) || (s.assigned_grade || '').toLowerCase().includes(term);
      })
    : students;

  // Group students by grade (using the school's configured grade order when available)
  const gradeOrderList = settings.grade_sequence && settings.grade_sequence.length > 0 ? settings.grade_sequence : null;
  const sortedStudents = [...filteredStudents].sort((a, b) => {
    const gradeA = a.assigned_grade || '';
    const gradeB = b.assigned_grade || '';
    if (gradeA !== gradeB) {
      if (gradeOrderList) {
        const ai = gradeOrderList.indexOf(gradeA);
        const bi = gradeOrderList.indexOf(gradeB);
        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      }
      return gradeA.localeCompare(gradeB);
    }
    const nameA = `${a.last_name_paternal || ''} ${a.first_name || ''}`;
    const nameB = `${b.last_name_paternal || ''} ${b.first_name || ''}`;
    return nameA.localeCompare(nameB);
  });

  const deleteSelectedStudents = async () => {
    if (selectedStudents.length === 0) {
      toast.error('No hay estudiantes seleccionados');
      return;
    }

    const confirmed = window.confirm(
      `¿Está seguro de que desea eliminar ${selectedStudents.length} estudiante(s)? Esta acción no se puede deshacer.`
    );

    if (confirmed) {
      try {
        setLoading(true);
        const response = await axios.post('/students/delete-multiple', {
          student_ids: selectedStudents
        });

        toast.success(`${response.data.deleted_count} estudiantes eliminados exitosamente`);
        
        if (response.data.errors && response.data.errors.length > 0) {
          response.data.errors.forEach(error => toast.error(error));
        }

        setSelectedStudents([]);
        setSelectAll(false);
        fetchStudents();
        fetchDashboardStats();
      } catch (error) {
        console.error('Error deleting students:', error);
        toast.error(handleValidationError(error));
      } finally {
        setLoading(false);
      }
    }
  };

  const deleteStudent = async (studentId) => {
    const student = students.find(s => s.id === studentId);
    const confirmed = window.confirm(
      `¿Está seguro de que desea eliminar al estudiante ${student?.first_name} ${student?.last_name_paternal}? Esta acción no se puede deshacer.`
    );

    if (confirmed) {
      try {
        setLoading(true);
        await axios.delete(`/students/${studentId}`);
        toast.success('Estudiante eliminado exitosamente');
        fetchStudents();
        fetchDashboardStats();
      } catch (error) {
        console.error('Error deleting student:', error);
        toast.error(handleValidationError(error));
      } finally {
        setLoading(false);
      }
    }
  };

  const withdrawStudent = async (studentId, reason = '') => {
    const student = students.find(s => s.id === studentId);
    const withdrawalReason = reason || window.prompt(
      `¿Por qué motivo se da de baja a ${student?.first_name} ${student?.last_name_paternal}?`,
      'Cambio de escuela'
    );

    if (withdrawalReason !== null) {
      try {
        setLoading(true);
        await axios.put(`/students/${studentId}/withdraw`, {
          reason: withdrawalReason
        });
        toast.success('Estudiante marcado como baja exitosamente');
        fetchStudents();
        fetchDashboardStats();
        if (showWithdrawn) await fetchWithdrawnStudents();
      } catch (error) {
        console.error('Error withdrawing student:', error);
        toast.error(handleValidationError(error));
      } finally {
        setLoading(false);
      }
    }
  };

  const fetchWithdrawnStudents = async () => {
    try {
      const response = await axios.get('/students/withdrawn');
      setWithdrawnStudents(response.data);
    } catch (error) {
      console.error('Error fetching withdrawn students:', error);
      toast.error('Error al cargar estudiantes dados de baja');
    }
  };

  const toggleWithdrawnView = async () => {
    const next = !showWithdrawn;
    setShowWithdrawn(next);
    if (next) await fetchWithdrawnStudents();
  };

  const reactivateStudent = async (studentId) => {
    try {
      setLoading(true);
      await axios.put(`/students/${studentId}/reactivate`);
      toast.success('Estudiante reactivado exitosamente');
      fetchStudents();
      fetchDashboardStats();
      if (showWithdrawn) await fetchWithdrawnStudents();
    } catch (error) {
      console.error('Error reactivating student:', error);
      toast.error(handleValidationError(error));
    } finally {
      setLoading(false);
    }
  };

  const sendParentCredentials = async (student) => {
    const hasParents = student.mother_user_id || student.father_user_id;
    if (!hasParents) {
      toast.error('Este estudiante no tiene cuentas de padres asociadas');
      return;
    }
    const confirmed = window.confirm(
      `¿Enviar credenciales a los padres de ${student.first_name} ${student.last_name_paternal}?\n\nSolo se enviarán a las cuentas que aún no han recibido sus credenciales.`
    );
    if (!confirmed) return;
    try {
      setLoading(true);
      const res = await axios.post(`/students/${student.id}/send-credentials`, { account: 'both' });
      const { results = [], sent_count, skipped_count } = res.data;
      if (sent_count === 0 && skipped_count > 0) {
        toast.info('Todas las cuentas ya recibieron sus credenciales anteriormente.');
        return;
      }
      const summary = results.map(r => {
        const status = r.sent ? 'Enviado' : (r.email ? 'SMTP falló' : 'Sin email registrado');
        return `${r.role}: ${r.username} / ${r.new_password} (${status})`;
      }).join('\n');
      const skipMsg = skipped_count > 0 ? `\n(${skipped_count} cuenta(s) ya tenían credenciales enviadas)` : '';
      toast.success(`Credenciales enviadas:\n${summary}${skipMsg}`, { duration: 20000 });
    } catch (error) {
      console.error('Error sending credentials:', error);
      toast.error(handleValidationError(error));
    } finally {
      setLoading(false);
    }
  };

  const sendBulkParentCredentials = async () => {
    const confirmed = window.confirm(
      '¿Enviar credenciales a TODOS los padres que aún no las han recibido?\n\nEsto generará una contraseña nueva y enviará un correo SOLO a las cuentas que nunca han recibido sus credenciales. Las cuentas que ya las recibieron se omitirán.'
    );
    if (!confirmed) return;
    try {
      setLoading(true);
      const res = await axios.post('/students/send-credentials-bulk');
      const { total_pending = 0, sent_count = 0, failed_count = 0, results = [] } = res.data || {};
      if (total_pending === 0) {
        toast.info('No hay cuentas pendientes. Todos los padres ya recibieron sus credenciales.');
        return;
      }
      if (failed_count === 0) {
        toast.success(`✓ Credenciales enviadas a ${sent_count} padre(s)`, { duration: 10000 });
      } else {
        toast.warning(`${sent_count} enviados, ${failed_count} fallidos`, { duration: 15000 });
        const errors = results.filter(r => !r.sent).map(r => `• ${r.username || 'sin usuario'} (${r.email || 'sin email'}): ${r.error}`).join('\n');
        if (errors) {
          setTimeout(() => alert(`Fallos:\n${errors}`), 300);
        }
      }
    } catch (error) {
      console.error('Error bulk sending credentials:', error);
      toast.error(handleValidationError(error));
    } finally {
      setLoading(false);
    }
  };

  const resetParentCredentials = async (student) => {
    const hasParents = student.mother_user_id || student.father_user_id;
    if (!hasParents) {
      toast.error('Este estudiante no tiene cuentas de padres asociadas');
      return;
    }
    const confirmed = window.confirm(
      `⚠️ ¿Resetear contraseñas de los padres de ${student.first_name} ${student.last_name_paternal}?\n\nEsto GENERARÁ CONTRASEÑAS NUEVAS y las enviará por correo a ambas cuentas (madre y padre), incluso si ya las habían recibido antes. Las contraseñas actuales dejarán de funcionar.`
    );
    if (!confirmed) return;
    try {
      setLoading(true);
      const res = await axios.post(`/students/${student.id}/resend-credentials`, { account: 'both' });
      const { results = [] } = res.data || {};
      const summary = results.map(r => {
        const status = r.sent ? 'Enviado' : (r.email ? 'SMTP falló' : 'Sin email registrado');
        return `${r.role}: ${r.username} / ${r.new_password} (${status})`;
      }).join('\n');
      toast.success(`Contraseñas reseteadas:\n${summary}`, { duration: 20000 });
    } catch (error) {
      console.error('Error resetting credentials:', error);
      toast.error(handleValidationError(error));
    } finally {
      setLoading(false);
    }
  };

  // Form submission handlers
  const handleStudentSubmit = async (e) => {
    e.preventDefault();
    
    if (studentForm.create_parent_account && !editingStudent) {
      const motherEmail = studentForm.mother_email || studentForm.email;
      if (!motherEmail || !motherEmail.includes('@')) {
        toast.error('Debe proporcionar un email válido para la cuenta principal');
        return;
      }
    }
    if (studentForm.create_father_account && !editingStudent) {
      const fatherEmail = studentForm.father_email || studentForm.email;
      if (!fatherEmail || !fatherEmail.includes('@')) {
        toast.error('Debe proporcionar un email válido para la cuenta secundaria');
        return;
      }
    }
    
    setLoading(true);

    try {
      if (editingStudent) {
        await axios.put(`/students/${editingStudent.id}`, studentForm);
        toast.success('Estudiante actualizado exitosamente');
      } else {
        // The UI uses `mother_email`/`father_email` state as generic
        // "Primary email" / "Secondary email" inputs. Map them to the
        // correct parent role before sending so the backend emails reach
        // the right person.
        const primaryEmail = studentForm.mother_email;
        const secondaryEmail = studentForm.father_email;
        const motherEmailPayload =
          studentForm.primary_parent_role === 'mother'
            ? primaryEmail
            : (studentForm.create_father_account && studentForm.secondary_parent_role === 'mother' ? secondaryEmail : '');
        const fatherEmailPayload =
          studentForm.primary_parent_role === 'father'
            ? primaryEmail
            : (studentForm.create_father_account && studentForm.secondary_parent_role === 'father' ? secondaryEmail : '');

        const payload = {
          ...studentForm,
          mother_email: motherEmailPayload,
          father_email: fatherEmailPayload,
        };
        const res = await axios.post('/students', payload);
        const creds = res.data.parent_credentials;
        if (creds && creds.length > 0) {
          const credText = creds.map(c => c.existing
            ? `${c.role}: Usuario existente: ${c.username} (ya vinculado a este hijo/a)`
            : `${c.role}: Usuario: ${c.username} / Contraseña: ${c.password}`).join('\n');
          toast.success(`Estudiante creado. Credenciales:\n${credText}`, { duration: 15000 });
        } else {
          toast.success('Estudiante creado exitosamente');
        }
      }
      
      setShowStudentDialog(false);
      setEditingStudent(null);
      setStudentForm({
        first_name: '',
        middle_name: '',
        last_name_paternal: '',
        last_name_maternal: '',
        birth_date: '',
        ssn_last_4: '',
        father_full_name: '',
        mother_full_name: '',
        phone_number: '',
        residential_address: '',
        postal_address: '',
        email: '',
        assigned_grade: '',
        medical_conditions: [],
        emergency_contact_name: '',
        emergency_contact_phone: '',
        create_parent_account: true,
        create_father_account: false,
        mother_email: '',
        father_email: '',
        parent_email: '',
        primary_parent_role: 'mother',
        secondary_parent_role: 'father'
      });
      fetchStudents();
      fetchDashboardStats();
    } catch (error) {
      console.error('Error saving student:', error);
      toast.error(handleValidationError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleTeacherSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingTeacher) {
        // Update existing teacher
        await axios.put(`/teachers/${editingTeacher.id}`, teacherForm);
        toast.success('Profesor actualizado exitosamente');
      } else {
        // Create new teacher
        await axios.post('/teachers', teacherForm);
        toast.success('Profesor creado exitosamente');
      }
      
      setShowTeacherDialog(false);
      setEditingTeacher(null);
      setTeacherForm({
        full_name: '',
        homeroom_grade: null,
        assigned_grades: [],
        subjects: [],
        phone: '',
        address: '',
        username: '',
        email: '',
        password: ''
      });
      fetchTeachers();
      fetchDashboardStats();
    } catch (error) {
      console.error('Error saving teacher:', error);
      toast.error(handleValidationError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleSubjectSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare data with proper types
      const submitData = {
        ...subjectForm,
        credits: subjectForm.credits ? parseInt(subjectForm.credits) : null,
        assigned_teacher_id: subjectForm.assigned_teacher_id === 'unassigned' || !subjectForm.assigned_teacher_id ? null : subjectForm.assigned_teacher_id
      };

      if (editingSubject) {
        // Update existing subject
        await axios.put(`/subjects/${editingSubject.id}`, submitData);
        toast.success('Materia actualizada exitosamente');
      } else {
        // Create new subject
        await axios.post('/subjects', submitData);
        toast.success('Materia creada exitosamente');
      }
      
      setShowSubjectDialog(false);
      setEditingSubject(null);
      setSubjectForm({
        name: '',
        code: '',
        description: '',
        credits: '',
        is_subclass: false,
        assigned_grades: [],
        assigned_teacher_id: ''
      });
      fetchSubjects();
      fetchDashboardStats();
    } catch (error) {
      console.error('Error saving subject:', error);
      toast.error(handleValidationError(error));
    } finally {
      setLoading(false);
    }
  };

  // Edit functions
  const editStudent = (student) => {
    setEditingStudent(student);
    setStudentForm({
      first_name: student.first_name || '',
      middle_name: student.middle_name || '',
      last_name_paternal: student.last_name_paternal || '',
      last_name_maternal: student.last_name_maternal || '',
      birth_date: student.birth_date ? student.birth_date.split('T')[0] : '',
      ssn_last_4: student.ssn_last_4 || '',
      father_full_name: student.father_full_name || '',
      mother_full_name: student.mother_full_name || '',
      phone_number: student.phone_number || '',
      residential_address: student.residential_address || '',
      postal_address: student.postal_address || '',
      email: student.email || '',
      assigned_grade: student.assigned_grade || '',
      medical_conditions: student.medical_conditions || [],
      emergency_contact_name: student.emergency_contact_name || '',
      emergency_contact_phone: student.emergency_contact_phone || '',
      monthly_tuition: student.monthly_tuition ?? 0,
      create_parent_account: false,
      parent_email: ''
    });
    setShowStudentDialog(true);
  };

  const editTeacher = (teacher) => {
    setEditingTeacher(teacher);
    setTeacherForm({
      full_name: teacher.full_name || '',
      homeroom_grade: teacher.homeroom_grade || null,
      assigned_grades: teacher.assigned_grades || [],
      subjects: teacher.subjects || [],
      phone: teacher.phone || '',
      address: teacher.address || '',
      username: teacher.username || '',
      email: teacher.email || '',
      password: '' // Don't pre-fill password for security
    });
    setShowTeacherDialog(true);
  };

  const editSubject = (subject) => {
    setEditingSubject(subject);
    setSubjectForm({
      name: subject.name || '',
      code: subject.code || '',
      description: subject.description || '',
      credits: subject.credits?.toString() || '',
      is_subclass: !!subject.is_subclass,
      assigned_grades: subject.assigned_grades || [],
      assigned_teacher_id: subject.assigned_teacher_id || ''
    });
    setShowSubjectDialog(true);
  };

  const deleteTeacher = async (teacherId) => {
    const teacher = teachers.find(t => t.id === teacherId);
    const confirmed = window.confirm(
      `¿Está seguro de que desea eliminar al profesor ${teacher?.full_name}? Esta acción no se puede deshacer.`
    );

    if (confirmed) {
      try {
        setLoading(true);
        await axios.delete(`/teachers/${teacherId}`);
        toast.success('Profesor eliminado exitosamente');
        fetchTeachers();
        fetchDashboardStats();
      } catch (error) {
        console.error('Error deleting teacher:', error);
        toast.error(handleValidationError(error));
      } finally {
        setLoading(false);
      }
    }
  };

  const deleteSubject = async (subjectId) => {
    const subject = subjects.find(s => s.id === subjectId);
    const confirmed = window.confirm(
      `¿Está seguro de que desea eliminar la materia ${subject?.name}? Esta acción no se puede deshacer.`
    );

    if (confirmed) {
      try {
        setLoading(true);
        await axios.delete(`/subjects/${subjectId}`);
        toast.success('Materia eliminada exitosamente');
        fetchSubjects();
        fetchDashboardStats();
      } catch (error) {
        console.error('Error deleting subject:', error);
        toast.error(handleValidationError(error));
      } finally {
        setLoading(false);
      }
    }
  };

  // Effects
  useEffect(() => {
    fetchDashboardStats();
    fetchStudents();
    fetchTeachers();
    fetchSubjects();
    fetchAcademicYears();
    fetchBlocks();
    // Auto-check for expired academic years
    axios.post('/academic-years/check-closure').then(res => {
      if (res.data.closed_years && res.data.closed_years.length > 0) {
        toast.info(`Ano(s) academico(s) cerrado(s) automaticamente: ${res.data.closed_years.join(', ')}`);
        fetchAcademicYears();
        fetchDashboardStats();
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (activeTab === 'settings') {
      fetchSettings();
    }
  }, [activeTab]);

  useEffect(() => {
    if (students.length > 0) {
      setSelectAll(selectedStudents.length === students.length);
    }
  }, [selectedStudents, students]);


  useEffect(() => {
    if (activeTab === 'income') {
      fetchIncomeStats();
    }
    if (activeTab === 'announcements') {
      fetchAnnouncements();
    }
  }, [activeTab, selectedYear, selectedMonth]);


  const grades = ['Preescolar', 'Kinder', '1ro', '2do', '3ro', '4to', '5to', '6to', '7mo', '8vo', '9no', '10mo', '11mo', '12vo'];

  return (
    <div className="min-h-screen bg-transparent">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`dashboard-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-content">
          <div className="p-5 pb-3">
            <div className="flex items-center mb-5">
              <div className="w-14 h-14 rounded-xl mr-3 flex items-center justify-center"
                style={schoolInfo?.school_logo_url
                  ? { background: '#ffffff', padding: '3px' }
                  : (schoolInfo?.school_accent_color
                      ? { background: `linear-gradient(135deg, ${schoolInfo.school_accent_color}, ${schoolInfo.school_primary_color})` }
                      : { background: 'linear-gradient(135deg, #f97316, #ea580c)' })}>
                {schoolInfo?.school_logo_url ? (
                  <img src={schoolInfo.school_logo_url.startsWith('data:') ? schoolInfo.school_logo_url : `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}${schoolInfo.school_logo_url}`} alt="" className="w-full h-full object-contain rounded-lg" />
                ) : (
                  <GraduationCap className="w-7 h-7 text-white" />
                )}
              </div>
              <div>
                <h1 className="text-base font-bold text-white tracking-tight">
                  {schoolInfo?.school_name || (
                    <span>
                      EduCore
                      <span className="relative inline-block">
                        <span>®</span>
                        <span className="absolute left-full bottom-1/2 text-[6px] font-bold tracking-[0.1em] text-slate-400 leading-none ml-0.5">BRAND</span>
                      </span>
                    </span>
                  )}
                </h1>
                <p className="text-[11px] text-slate-400 font-medium">Sistema de Gestión</p>
              </div>
            </div>
            
            <Button 
              onClick={logout}
              variant="outline" 
              size="sm"
              className="w-full bg-white/5 border-white/10 text-slate-300 hover:bg-red-500/20 hover:text-red-300 hover:border-red-400/30 transition-all text-xs mb-2"
            >
              <LogOut className="w-3 h-3 mr-1" />
              Cerrar Sesión
            </Button>
          </div>

          <div className="sidebar-nav">
            <SchoolNavigation activeTab={activeTab} schoolId={user?.school_id} canSeeAcademic={canSeeAcademic} canSeeFinance={canSeeFinance} canSeeConfig={canSeeConfig} onNavigate={id=>{setActiveTab(id);setSidebarOpen(false);}} />
          </div>

          <div className="sidebar-footer">
            <div className="bg-white/5 rounded-xl p-3 border border-white/5">
              <p className="text-xs text-white font-medium">{user?.username}</p>
              <p className="text-[11px] text-slate-400">
                {subrole === 'secretary' ? 'Secretaría'
                  : subrole === 'accounting' ? 'Contabilidad'
                  : 'Administrador del Sistema'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="dashboard-main">
        <div className="mb-8">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden fixed top-4 left-4 z-40 bg-blue-600 text-white p-2 rounded-lg shadow-lg"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          
          <div className="ml-14 lg:ml-0">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-1 h-8 bg-gradient-to-b from-orange-400 to-orange-600 rounded-full"></div>
              <h1 className="text-2xl font-bold text-slate-800 tracking-tight">
                Dashboard Administrativo
              </h1>
            </div>
            <p className="text-sm text-slate-500 ml-5">
              Gestiona tu institución educativa desde un solo lugar
            </p>
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'workflows' && canSeeAcademic && <SchoolWorkflows />}
        {activeTab === 'activity' && <ManagementPanel />}
        {activeTab === 'overview' && (
          <DashboardOverview />
        )}

        {/* Students Tab */}
        {activeTab === 'students' && (
          <div className="space-y-6">
            <div className="flex flex-col space-y-4">
              <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <div className="w-1 h-6 bg-gradient-to-b from-blue-400 to-blue-600 rounded-full"></div>
                    <h2 className="text-xl font-bold text-slate-800">Gestión de Estudiantes</h2>
                  </div>
                  <p className="text-sm text-slate-500 ml-5">Administra los perfiles de estudiantes</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    className={showWithdrawn
                      ? "border-slate-300 bg-slate-100 text-slate-800"
                      : "border-slate-200 text-slate-600 hover:bg-slate-50"}
                    onClick={toggleWithdrawnView}
                    data-testid="toggle-withdrawn-btn"
                    title="Ver estudiantes dados de baja"
                  >
                    <Clock className="w-4 h-4 mr-1" />
                    {showWithdrawn ? 'Ocultar bajas' : 'Ver bajas'}
                  </Button>
                  <Button
                    variant="outline"
                    className="border-purple-200 text-purple-700 hover:bg-purple-50"
                    onClick={sendBulkParentCredentials}
                    data-testid="bulk-send-credentials-btn"
                    title="Enviar credenciales a todos los padres que aún no las han recibido"
                  >
                    <Send className="w-4 h-4 mr-1" /> Enviar a todos
                  </Button>
                  <Button
                    variant="outline"
                    className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                    onClick={async () => {
                      try {
                        const res = await axios.get('/students/import-template', { responseType: 'blob' });
                        const url = URL.createObjectURL(res.data);
                        const a = document.createElement('a');
                        a.href = url; a.download = 'plantilla_estudiantes.xlsx';
                        a.click(); URL.revokeObjectURL(url);
                      } catch {
                        toast.error('Error al descargar plantilla');
                      }
                    }}
                    data-testid="download-template-btn"
                  >
                    <Download className="w-4 h-4 mr-1" /> Plantilla
                  </Button>
                  <label htmlFor="import-students-input" className="cursor-pointer">
                    <div className="inline-flex items-center justify-center rounded-md border border-emerald-200 bg-white px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50 h-10">
                      <FileText className="w-4 h-4 mr-1" /> Importar Excel
                    </div>
                    <input
                      id="import-students-input"
                      type="file"
                      accept=".xlsx"
                      className="hidden"
                      data-testid="import-students-input"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        const fd = new FormData();
                        fd.append('file', f);
                        try {
                          const res = await axios.post('/students/import', fd, {
                            headers: { 'Content-Type': 'multipart/form-data' },
                          });
                          const { created_count, error_count, errors } = res.data;
                          setImportResult(res.data);
                          if (error_count === 0) {
                            toast.success(`✓ ${created_count} estudiante(s) importado(s)`);
                          } else {
                            toast.warning(`${created_count} creados, ${error_count} errores`);
                          }
                          fetchStudents?.();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Error al importar');
                        } finally {
                          e.target.value = '';
                        }
                      }}
                    />
                  </label>

                {/* Import Result Dialog */}
                <Dialog open={!!importResult} onOpenChange={(open) => { if (!open) setImportResult(null); }}>
                  <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="import-result-dialog">
                    <DialogHeader>
                      <DialogTitle>Resultado de la importación</DialogTitle>
                      <DialogDescription>
                        {importResult?.created_count || 0} estudiante(s) creado(s) · {importResult?.error_count || 0} error(es).
                        Guarda estas credenciales: las contraseñas solo se muestran una vez.
                      </DialogDescription>
                    </DialogHeader>

                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="download-import-report-btn"
                        onClick={async () => {
                          try {
                            const res = await axios.post('/students/import-report', { created: importResult?.created || [] }, { responseType: 'blob' });
                            const url = URL.createObjectURL(res.data);
                            const a = document.createElement('a');
                            a.href = url; a.download = 'informe_cuentas_padres.xlsx';
                            a.click(); URL.revokeObjectURL(url);
                          } catch {
                            toast.error('Error al generar el informe');
                          }
                        }}
                      >
                        <Download className="w-4 h-4 mr-1" /> Descargar informe (Excel)
                      </Button>
                    </div>

                    <div className="space-y-3 mt-2">
                      {(importResult?.created || []).map((s) => (
                        <div key={s.row} className="border rounded-lg p-3 bg-slate-50" data-testid={`import-student-${s.row}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-semibold text-slate-800">{s.name}</span>
                            <Badge variant="outline">{s.student_number}</Badge>
                          </div>
                          {(s.parents && s.parents.length > 0) ? (
                            <div className="space-y-1">
                              {s.parents.map((p, idx) => (
                                <div key={idx} className="text-sm flex flex-wrap gap-x-4 gap-y-1 items-center">
                                  <span className="font-medium text-slate-600 w-16">{p.role}:</span>
                                  <span>👤 <span className="font-mono">{p.username}</span></span>
                                  {p.existing
                                    ? <span className="text-amber-600 text-xs">(cuenta existente vinculada)</span>
                                    : <span>🔑 <span className="font-mono">{p.password}</span></span>}
                                  {p.email && <span className="text-slate-400 text-xs">{p.email}</span>}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-slate-400">Sin cuentas de padres (no se indicó nombre de madre/padre).</p>
                          )}
                        </div>
                      ))}

                      {(importResult?.errors || []).length > 0 && (
                        <div className="border border-red-200 rounded-lg p-3 bg-red-50">
                          <p className="font-semibold text-red-700 text-sm mb-1">Errores</p>
                          {importResult.errors.map((er, idx) => (
                            <p key={idx} className="text-xs text-red-600">Fila {er.row}: {er.error}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
                <Dialog open={showStudentDialog} onOpenChange={(open) => {
                  setShowStudentDialog(open);
                  if (!open) {
                    setEditingStudent(null);
                    setStudentForm({
                      first_name: '',
                      middle_name: '',
                      last_name_paternal: '',
                      last_name_maternal: '',
                      birth_date: '',
                      ssn_last_4: '',
                      father_full_name: '',
                      mother_full_name: '',
                      phone_number: '',
                      residential_address: '',
                      postal_address: '',
                      email: '',
                      assigned_grade: '',
                      medical_conditions: [],
                      emergency_contact_name: '',
                      emergency_contact_phone: '',
                      create_parent_account: true,
                      parent_email: '',
                      primary_parent_role: 'mother',
                      secondary_parent_role: 'father'
                    });
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 shadow-sm">
                      <Plus className="w-4 h-4 mr-2" />
                      Agregar Estudiante
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>
                        {editingStudent ? 'Editar Estudiante' : 'Agregar Nuevo Estudiante'}
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleStudentSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label htmlFor="first_name">Primer Nombre *</Label>
                          <Input
                            id="first_name"
                            value={studentForm.first_name}
                            onChange={(e) => setStudentForm({...studentForm, first_name: e.target.value})}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="middle_name">Segundo Nombre</Label>
                          <Input
                            id="middle_name"
                            value={studentForm.middle_name}
                            onChange={(e) => setStudentForm({...studentForm, middle_name: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label htmlFor="last_name_paternal">Apellido Paterno *</Label>
                          <Input
                            id="last_name_paternal"
                            value={studentForm.last_name_paternal}
                            onChange={(e) => setStudentForm({...studentForm, last_name_paternal: e.target.value})}
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="last_name_maternal">Apellido Materno *</Label>
                        <Input
                          id="last_name_maternal"
                          value={studentForm.last_name_maternal}
                          onChange={(e) => setStudentForm({...studentForm, last_name_maternal: e.target.value})}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="birth_date">Fecha de Nacimiento *</Label>
                        <Input
                          id="birth_date"
                          type="date"
                          value={studentForm.birth_date}
                          onChange={(e) => setStudentForm({...studentForm, birth_date: e.target.value})}
                          required
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="father_full_name">Nombre Completo del Padre *</Label>
                          <Input
                            id="father_full_name"
                            value={studentForm.father_full_name}
                            onChange={(e) => setStudentForm({...studentForm, father_full_name: e.target.value})}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="mother_full_name">Nombre Completo de la Madre *</Label>
                          <Input
                            id="mother_full_name"
                            value={studentForm.mother_full_name}
                            onChange={(e) => setStudentForm({...studentForm, mother_full_name: e.target.value})}
                            required
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="phone_number">Teléfono *</Label>
                          <Input
                            id="phone_number"
                            value={studentForm.phone_number}
                            onChange={(e) => setStudentForm({...studentForm, phone_number: e.target.value})}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="email">Email *</Label>
                          <Input
                            id="email"
                            type="email"
                            value={studentForm.email}
                            onChange={(e) => setStudentForm({...studentForm, email: e.target.value})}
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="residential_address">Dirección Residencial *</Label>
                        <Textarea
                          id="residential_address"
                          value={studentForm.residential_address}
                          onChange={(e) => setStudentForm({...studentForm, residential_address: e.target.value})}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="postal_address">Dirección Postal *</Label>
                        <Textarea
                          id="postal_address"
                          value={studentForm.postal_address}
                          onChange={(e) => setStudentForm({...studentForm, postal_address: e.target.value})}
                          required
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="assigned_grade">Grado *</Label>
                          <Select value={studentForm.assigned_grade} onValueChange={(value) => setStudentForm({...studentForm, assigned_grade: value})}>
                            <SelectTrigger>
                              <SelectValue placeholder="Seleccionar grado" />
                            </SelectTrigger>
                            <SelectContent>
                              {grades.map((grade) => (
                                <SelectItem key={grade} value={grade}>{grade}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="emergency_contact_name">Contacto de Emergencia</Label>
                          <Input
                            id="emergency_contact_name"
                            value={studentForm.emergency_contact_name}
                            onChange={(e) => setStudentForm({...studentForm, emergency_contact_name: e.target.value})}
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="emergency_contact_phone">Teléfono de Emergencia</Label>
                        <Input
                          id="emergency_contact_phone"
                          value={studentForm.emergency_contact_phone}
                          onChange={(e) => setStudentForm({...studentForm, emergency_contact_phone: e.target.value})}
                        />
                      </div>

                      <div>
                        <Label htmlFor="monthly_tuition">Mensualidad ($) <span className="text-slate-400 text-xs">(para facturación automática)</span></Label>
                        <Input
                          id="monthly_tuition"
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Ej: 150.00"
                          value={studentForm.monthly_tuition ?? ''}
                          onChange={(e) => setStudentForm({...studentForm, monthly_tuition: e.target.value === '' ? 0 : parseFloat(e.target.value)})}
                          data-testid="student-monthly-tuition"
                        />
                      </div>

                      <div>
                        <Label htmlFor="medical_conditions">Condiciones Médicas</Label>
                        <Textarea
                          id="medical_conditions"
                          value={(studentForm.medical_conditions || [])
                            .map(c => (typeof c === 'string' ? c : (c?.condition || '')))
                            .filter(Boolean)
                            .join(', ')}
                          onChange={(e) => setStudentForm({
                            ...studentForm,
                            medical_conditions: e.target.value
                              .split(',')
                              .map(s => s.trim())
                              .filter(Boolean)
                              .map(condition => ({ condition, severity: 'leve' })),
                          })}
                          placeholder="Ejemplo: Asma, Alergias a mariscos, Diabetes"
                          data-testid="medical-conditions-input"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Separa cada condición con una coma. Ejemplo: <em>Asma, Alergia al maní, Epilepsia</em>
                        </p>
                      </div>

                      {!editingStudent && (
                        <div className="space-y-3">
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id="create_parent_account"
                              checked={studentForm.create_parent_account}
                              onChange={(e) => setStudentForm({...studentForm, create_parent_account: e.target.checked})}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <Label htmlFor="create_parent_account" className="text-sm font-medium cursor-pointer">
                              Crear Cuenta Principal
                            </Label>
                          </div>
                          
                          {studentForm.create_parent_account && (
                            <div className="space-y-3 pl-6 border-l-2 border-orange-200">
                              <div>
                                <Label htmlFor="primary_parent_role" className="text-sm">
                                  Tipo de Cuenta Principal *
                                </Label>
                                <select
                                  id="primary_parent_role"
                                  data-testid="primary-parent-role-select"
                                  value={studentForm.primary_parent_role}
                                  onChange={(e) => setStudentForm({
                                    ...studentForm,
                                    primary_parent_role: e.target.value,
                                    // Auto-flip secondary to the opposite so both cannot be the same
                                    secondary_parent_role: e.target.value === 'mother' ? 'father' : 'mother'
                                  })}
                                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                                >
                                  <option value="mother">Madre</option>
                                  <option value="father">Padre</option>
                                </select>
                              </div>
                              <div>
                                <Label htmlFor="mother_email">
                                  Email Cuenta Principal *
                                </Label>
                                <Input
                                  id="mother_email"
                                  type="email"
                                  value={studentForm.mother_email}
                                  onChange={(e) => setStudentForm({...studentForm, mother_email: e.target.value})}
                                  placeholder={studentForm.primary_parent_role === 'father' ? 'Email del Padre' : 'Email de la Madre'}
                                  required
                                />
                                <p className="text-xs text-slate-400 mt-1">
                                  Se generarán credenciales automáticas (usuario basado en el nombre) y se enviarán por correo.
                                </p>
                              </div>
                            </div>
                          )}

                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id="create_father_account"
                              checked={studentForm.create_father_account}
                              onChange={(e) => setStudentForm({...studentForm, create_father_account: e.target.checked})}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <Label htmlFor="create_father_account" className="text-sm font-medium cursor-pointer">
                              Crear Cuenta Secundaria (opcional)
                            </Label>
                          </div>

                          {studentForm.create_father_account && (
                            <div className="space-y-3 pl-6 border-l-2 border-orange-200">
                              <div>
                                <Label htmlFor="secondary_parent_role" className="text-sm">
                                  Tipo de Cuenta Secundaria *
                                </Label>
                                <select
                                  id="secondary_parent_role"
                                  data-testid="secondary-parent-role-select"
                                  value={studentForm.secondary_parent_role}
                                  onChange={(e) => setStudentForm({
                                    ...studentForm,
                                    secondary_parent_role: e.target.value,
                                    primary_parent_role: e.target.value === 'mother' ? 'father' : 'mother'
                                  })}
                                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                                >
                                  <option value="father">Padre</option>
                                  <option value="mother">Madre</option>
                                </select>
                                <p className="text-xs text-slate-400 mt-1">
                                  Debe ser diferente al tipo de la cuenta principal.
                                </p>
                              </div>
                              <div>
                                <Label htmlFor="father_email">
                                  Email Cuenta Secundaria *
                                </Label>
                                <Input
                                  id="father_email"
                                  type="email"
                                  value={studentForm.father_email}
                                  onChange={(e) => setStudentForm({...studentForm, father_email: e.target.value})}
                                  placeholder={studentForm.secondary_parent_role === 'mother' ? 'Email de la Madre' : 'Email del Padre'}
                                  required
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="flex justify-end space-x-4">
                        <Button type="button" variant="outline" onClick={() => setShowStudentDialog(false)}>
                          Cancelar
                        </Button>
                        <Button type="submit" disabled={loading}>
                          {loading ? 
                            (editingStudent ? 'Actualizando...' : 'Creando...') : 
                            (editingStudent ? 'Actualizar Estudiante' : 'Crear Estudiante')
                          }
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
                </div>
              </div>
              
              {/* Bulk Actions */}
              {selectedStudents.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <span className="text-sm font-medium text-blue-900">
                        {selectedStudents.length} estudiante(s) seleccionado(s)
                      </span>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          setSelectedStudents([]);
                          setSelectAll(false);
                        }}
                      >
                        Limpiar selección
                      </Button>
                    </div>
                    <div className="flex space-x-2">
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={deleteSelectedStudents}
                        disabled={loading}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Eliminar Seleccionados
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Withdrawn Students View */}
            {showWithdrawn && (
              <Card className="modern-card border-amber-200" data-testid="withdrawn-students-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-amber-700">
                    <Clock className="w-5 h-5" />
                    Estudiantes Dados de Baja ({withdrawnStudents.length})
                  </CardTitle>
                  <CardDescription>
                    Consulta la información histórica y reactiva estudiantes retirados
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {withdrawnStudents.length === 0 ? (
                    <div className="text-center py-8 text-slate-400">
                      <Clock className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>No hay estudiantes dados de baja</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {withdrawnStudents.map((student) => (
                        <div
                          key={student.id}
                          className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200"
                          data-testid={`withdrawn-student-${student.id}`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-slate-800">
                                {student.first_name} {student.middle_name} {student.last_name_paternal} {student.last_name_maternal}
                              </p>
                              {student.student_number && (
                                <span className="text-xs text-blue-600 font-mono">{student.student_number}</span>
                              )}
                              <Badge variant="outline">{student.assigned_grade}</Badge>
                            </div>
                            <p className="text-sm text-slate-600 mt-1">
                              <span className="font-medium">Motivo:</span> {student.withdrawal_reason || 'No especificado'}
                            </p>
                            <p className="text-xs text-slate-400">
                              Baja: {student.withdrawn_date ? new Date(student.withdrawn_date).toLocaleString('es-PR') : 'N/A'}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedStudent(student);
                                setShowStudentDetails(true);
                              }}
                              title="Ver detalles"
                              data-testid={`view-withdrawn-${student.id}`}
                            >
                              <Eye className="w-3 h-3 mr-1" /> Ver
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => reactivateStudent(student.id)}
                              className="text-green-600 hover:text-green-700 bg-green-50 hover:bg-green-100"
                              disabled={loading}
                              title="Reactivar estudiante"
                              data-testid={`reactivate-withdrawn-${student.id}`}
                            >
                              <RefreshCw className="w-3 h-3 mr-1" /> Reactivar
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Desktop Table */}
            {!showWithdrawn && (
            <>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar estudiante por nombre, número o grado..."
                className="pl-10"
                value={studentSearch}
                onChange={(e) => setStudentSearch(e.target.value)}
                data-testid="student-search-input"
              />
            </div>
            <Card className="modern-card hidden md:block">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th className="w-12">
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                        </th>
                        <th>Nombre Completo</th>
                        <th>Grado</th>
                        <th className="hidden lg:table-cell">Email</th>
                        <th className="hidden lg:table-cell">Teléfono</th>
                        <th className="hidden xl:table-cell">Estado</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStudents.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center py-8 text-slate-400">
                            <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                            <p>{studentSearch ? 'No se encontraron estudiantes' : 'No hay estudiantes registrados'}</p>
                            <p className="text-sm">{studentSearch ? 'Intenta con otro término de búsqueda' : 'Haz clic en "Agregar Estudiante" para comenzar'}</p>
                          </td>
                        </tr>
                      ) : (
                        sortedStudents.map((student, idx) => {
                          const showGradeHeader = idx === 0 || sortedStudents[idx - 1].assigned_grade !== student.assigned_grade;
                          return (
                          <React.Fragment key={student.id}>
                          {showGradeHeader && (
                            <tr className="bg-blue-50" data-testid={`grade-group-header-${student.assigned_grade}`}>
                              <td colSpan="7" className="px-3 py-1.5 text-xs font-bold text-blue-700 uppercase tracking-wide">
                                {student.assigned_grade || 'Sin grado'}
                              </td>
                            </tr>
                          )}
                          <tr className={student.is_withdrawn ? 'bg-transparent opacity-75' : ''}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedStudents.includes(student.id)}
                                onChange={() => handleSelectStudent(student.id)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                            </td>
                            <td>
                              <div>
                                <p className="font-medium text-slate-800">
                                  {student.first_name} {student.middle_name} {student.last_name_paternal}
                                </p>
                                <p className="text-sm text-slate-400">{student.last_name_maternal}</p>
                                {student.student_number && (
                                  <p className="text-xs text-blue-600 font-mono">{student.student_number}</p>
                                )}
                                {(parentActivity[student.id]?.mother || parentActivity[student.id]?.father) && (
                                  <div className="mt-1 flex flex-col gap-0.5">
                                    {parentActivity[student.id]?.mother && (
                                      <span
                                        className={`text-[10px] ${getLastLoginColor(parentActivity[student.id].mother.last_login_at)}`}
                                        title={parentActivity[student.id].mother.last_login_at ? new Date(parentActivity[student.id].mother.last_login_at).toLocaleString('es-PR') : 'Nunca conectada'}
                                        data-testid={`mother-last-login-${student.id}`}
                                      >
                                        👩 Madre: {formatLastLogin(parentActivity[student.id].mother.last_login_at)}
                                      </span>
                                    )}
                                    {parentActivity[student.id]?.father && (
                                      <span
                                        className={`text-[10px] ${getLastLoginColor(parentActivity[student.id].father.last_login_at)}`}
                                        title={parentActivity[student.id].father.last_login_at ? new Date(parentActivity[student.id].father.last_login_at).toLocaleString('es-PR') : 'Nunca conectado'}
                                        data-testid={`father-last-login-${student.id}`}
                                      >
                                        👨 Padre: {formatLastLogin(parentActivity[student.id].father.last_login_at)}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </td>
                          <td>
                            <Badge variant="outline">{student.assigned_grade}</Badge>
                          </td>
                          <td className="hidden lg:table-cell">
                            <div className="flex items-center">
                              <Mail className="w-4 h-4 mr-2 text-gray-400" />
                              <span className="truncate max-w-[150px]">{student.email}</span>
                            </div>
                          </td>
                          <td className="hidden lg:table-cell">
                            <div className="flex items-center">
                              <Phone className="w-4 h-4 mr-2 text-gray-400" />
                              <span>{student.phone_number}</span>
                            </div>
                          </td>
                          <td className="hidden xl:table-cell">
                            {student.is_withdrawn ? (
                              <Badge variant="destructive">Baja</Badge>
                            ) : (
                              <Badge variant="default">Activo</Badge>
                            )}
                          </td>
                          <td>
                            <div className="flex space-x-1">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  setSelectedStudent(student);
                                  setShowStudentDetails(true);
                                }}
                                title="Ver detalles"
                              >
                                <Eye className="w-3 h-3" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => editStudent(student)}
                                title="Editar"
                                className="text-blue-600 hover:text-blue-700"
                              >
                                <Edit className="w-3 h-3" />
                              </Button>
                              {(student.mother_user_id || student.father_user_id) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => sendParentCredentials(student)}
                                  title="Enviar credenciales a cuentas nuevas"
                                  className="text-purple-600 hover:text-purple-700"
                                  data-testid={`send-credentials-${student.id}`}
                                >
                                  <Send className="w-3 h-3" />
                                </Button>
                              )}
                              {(student.mother_user_id || student.father_user_id) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => resetParentCredentials(student)}
                                  title="Resetear contraseñas de los padres (genera y reenvía)"
                                  className="text-amber-600 hover:text-amber-700"
                                  data-testid={`reset-credentials-${student.id}`}
                                >
                                  <RefreshCw className="w-3 h-3" />
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  setSelectedStudentForBlock(student.id);
                                  setShowBlockDialog(true);
                                }}
                                title="Bloquear estudiante"
                                className="text-red-600 hover:text-red-700"
                              >
                                <AlertTriangle className="w-3 h-3" />
                              </Button>
                              {student.is_withdrawn ? (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => reactivateStudent(student.id)}
                                  title="Reactivar"
                                  className="text-green-600 hover:text-green-700"
                                >
                                  <RefreshCw className="w-3 h-3" />
                                </Button>
                              ) : (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => withdrawStudent(student.id)}
                                  title="Dar de baja"
                                  className="text-yellow-600 hover:text-yellow-700"
                                >
                                  <Clock className="w-3 h-3" />
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => deleteStudent(student.id)}
                                title="Eliminar permanentemente"
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                        </React.Fragment>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Mobile student cards */}
            <div className="md:hidden space-y-3" data-testid="students-mobile-list">
              {filteredStudents.length === 0 ? (
                <Card className="modern-card">
                  <CardContent className="text-center py-8 text-slate-400">
                    <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>{studentSearch ? 'No se encontraron estudiantes' : 'No hay estudiantes registrados'}</p>
                  </CardContent>
                </Card>
              ) : (
                sortedStudents.map((student, idx) => {
                  const showGradeHeader = idx === 0 || sortedStudents[idx - 1].assigned_grade !== student.assigned_grade;
                  return (
                  <React.Fragment key={student.id}>
                  {showGradeHeader && (
                    <p className="text-xs font-bold text-blue-700 uppercase tracking-wide bg-blue-50 rounded-md px-3 py-1.5 mt-2">
                      {student.assigned_grade || 'Sin grado'}
                    </p>
                  )}
                  <Card className={`modern-card ${student.is_withdrawn ? 'opacity-75' : ''}`} data-testid={`student-card-${student.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-slate-800 break-words">
                            {student.first_name} {student.middle_name} {student.last_name_paternal} {student.last_name_maternal}
                          </p>
                          {student.student_number && (
                            <p className="text-xs text-blue-600 font-mono">{student.student_number}</p>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline">{student.assigned_grade}</Badge>
                            {student.is_withdrawn ? (
                              <Badge variant="destructive">Baja</Badge>
                            ) : (
                              <Badge variant="default">Activo</Badge>
                            )}
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          checked={selectedStudents.includes(student.id)}
                          onChange={() => handleSelectStudent(student.id)}
                          className="w-4 h-4 mt-1 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </div>
                      <div className="flex flex-wrap gap-1 mt-3">
                        <Button size="sm" variant="outline" onClick={() => { setSelectedStudent(student); setShowStudentDetails(true); }} title="Ver detalles" data-testid={`mobile-view-${student.id}`}>
                          <Eye className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => editStudent(student)} title="Editar" className="text-blue-600">
                          <Edit className="w-3 h-3" />
                        </Button>
                        {(student.mother_user_id || student.father_user_id) && (
                          <Button size="sm" variant="outline" onClick={() => sendParentCredentials(student)} title="Enviar credenciales" className="text-purple-600">
                            <Send className="w-3 h-3" />
                          </Button>
                        )}
                        {student.is_withdrawn ? (
                          <Button size="sm" variant="outline" onClick={() => reactivateStudent(student.id)} title="Reactivar" className="text-green-600">
                            <RefreshCw className="w-3 h-3" />
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => withdrawStudent(student.id)} title="Dar de baja" className="text-yellow-600">
                            <Clock className="w-3 h-3" />
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => deleteStudent(student.id)} title="Eliminar" className="text-red-600">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                  </React.Fragment>
                  );
                })
              )}
            </div>

            </>
            )}

            {/* Bloqueos Activos */}
            {blocks.length > 0 && (
              <Card className="modern-card border-red-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-600">
                    <AlertTriangle className="w-5 h-5" />
                    Estudiantes Bloqueados ({blocks.filter(b => b.is_active).length})
                  </CardTitle>
                  <CardDescription>
                    Estudiantes con acceso restringido al sistema
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {blocks.filter(b => b.is_active).map((block) => {
                      const student = students.find(s => s.id === block.student_id);
                      return (
                        <div key={block.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                          <div className="flex-1">
                            <p className="font-medium text-slate-800">
                              {student ? `${student.first_name} ${student.last_name_paternal}` : 'Estudiante no encontrado'}
                            </p>
                            <p className="text-sm text-slate-500">Razón: {block.reason}</p>
                            <p className="text-xs text-slate-400">
                              Bloqueado: {new Date(block.created_at).toLocaleDateString('es-ES')}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUnblockStudent(block.id)}
                            className="bg-green-50 text-green-600 hover:bg-green-100"
                          >
                            Desbloquear
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Diálogo de Bloqueo */}
            <Dialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Bloquear Estudiante</DialogTitle>
                  <DialogDescription>
                    El estudiante no podrá acceder al sistema hasta que sea desbloqueado
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <Alert className="bg-red-50 border-red-200">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <AlertDescription className="text-red-800">
                      Esta acción restringirá el acceso del estudiante y sus padres al sistema.
                    </AlertDescription>
                  </Alert>
                  <div>
                    <Label htmlFor="block_reason">Razón del Bloqueo *</Label>
                    <Textarea
                      id="block_reason"
                      value={blockReason}
                      onChange={(e) => setBlockReason(e.target.value)}
                      placeholder="Ej: Falta de pago, conducta inapropiada, etc."
                      rows={4}
                      required
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowBlockDialog(false);
                      setBlockReason('');
                      setSelectedStudentForBlock('');
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleBlockStudent}
                    disabled={loading || !blockReason.trim()}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {loading ? 'Bloqueando...' : 'Bloquear Estudiante'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Teachers Tab */}
        {activeTab === 'teachers' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-1 h-6 bg-gradient-to-b from-orange-400 to-orange-600 rounded-full"></div>
                  <h2 className="text-xl font-bold text-slate-800">Gestión de Profesores</h2>
                </div>
                <p className="text-sm text-slate-500 ml-5">Administra los perfiles de profesores</p>
              </div>
              <Dialog open={showTeacherDialog} onOpenChange={(open) => {
                setShowTeacherDialog(open);
                if (!open) {
                  setEditingTeacher(null);
                  setTeacherForm({
                    full_name: '',
                    homeroom_grade: null,
                    assigned_grades: [],
                    subjects: [],
                    phone: '',
                    address: '',
                    username: '',
                    email: '',
                    password: ''
                  });
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 shadow-sm">
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Profesor
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>
                      {editingTeacher ? 'Editar Profesor' : 'Agregar Nuevo Profesor'}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleTeacherSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="teacher_full_name">Nombre Completo *</Label>
                        <Input
                          id="teacher_full_name"
                          value={teacherForm.full_name}
                          onChange={(e) => setTeacherForm({...teacherForm, full_name: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="teacher_phone">Teléfono</Label>
                        <Input
                          id="teacher_phone"
                          value={teacherForm.phone}
                          onChange={(e) => setTeacherForm({...teacherForm, phone: e.target.value})}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="teacher_email">Email *</Label>
                      <Input
                        id="teacher_email"
                        type="email"
                        value={teacherForm.email}
                        onChange={(e) => setTeacherForm({...teacherForm, email: e.target.value})}
                        required
                      />
                    </div>
                    
                    {!editingTeacher && (
                      <div>
                        <Label htmlFor="teacher_username">Usuario de Acceso *</Label>
                        <Input
                          id="teacher_username"
                          value={teacherForm.username}
                          onChange={(e) => setTeacherForm({...teacherForm, username: e.target.value})}
                          required
                        />
                      </div>
                    )}

                    {!editingTeacher && (
                      <div>
                        <Label htmlFor="teacher_password">Contraseña *</Label>
                        <Input
                          id="teacher_password"
                          type="password"
                          value={teacherForm.password}
                          onChange={(e) => setTeacherForm({...teacherForm, password: e.target.value})}
                          required
                        />
                      </div>
                    )}
                    
                    <div>
                      <Label>Grados Asignados</Label>
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        {grades.map((grade) => (
                          <div key={grade} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`grade-${grade}`}
                              checked={teacherForm.assigned_grades.includes(grade)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setTeacherForm({
                                    ...teacherForm, 
                                    assigned_grades: [...teacherForm.assigned_grades, grade]
                                  });
                                } else {
                                  setTeacherForm({
                                    ...teacherForm, 
                                    assigned_grades: teacherForm.assigned_grades.filter(g => g !== grade),
                                    homeroom_grade: teacherForm.homeroom_grade === grade ? null : teacherForm.homeroom_grade
                                  });
                                }
                              }}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <Label htmlFor={`grade-${grade}`} className="text-sm">{grade}</Label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <HomeroomSelect id="teacher-form-homeroom" grades={teacherForm.assigned_grades}
                      value={teacherForm.homeroom_grade} onChange={value => setTeacherForm({...teacherForm, homeroom_grade:value})} />
                    <div>
                      <Label>Materias Asignadas</Label>
                      {teacherForm.assigned_grades.length === 0 ? (
                        <p className="text-sm text-slate-400 mt-2">Selecciona primero los grados para ver las materias disponibles</p>
                      ) : (
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          {subjects
                            .filter(subject => 
                              subject.assigned_grades && 
                              subject.assigned_grades.some(grade => teacherForm.assigned_grades.includes(grade))
                            )
                            .map((subject) => (
                              <div key={subject.id} className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  id={`subject-${subject.id}`}
                                  checked={teacherForm.subjects.includes(subject.name)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setTeacherForm({
                                        ...teacherForm, 
                                        subjects: [...teacherForm.subjects, subject.name]
                                      });
                                    } else {
                                      setTeacherForm({
                                        ...teacherForm, 
                                        subjects: teacherForm.subjects.filter(s => s !== subject.name)
                                      });
                                    }
                                  }}
                                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <Label htmlFor={`subject-${subject.id}`} className="text-sm">
                                  {subject.name} ({subject.assigned_grades.join(', ')})
                                </Label>
                              </div>
                            ))}
                          {subjects.filter(subject => 
                            subject.assigned_grades && 
                            subject.assigned_grades.some(grade => teacherForm.assigned_grades.includes(grade))
                          ).length === 0 && (
                            <p className="text-sm text-slate-400 col-span-2">No hay materias disponibles para los grados seleccionados</p>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex justify-end space-x-4">
                      <Button type="button" variant="outline" onClick={() => setShowTeacherDialog(false)}>
                        Cancelar
                      </Button>
                      <Button type="submit" disabled={loading}>
                        {loading ? 
                          (editingTeacher ? 'Actualizando...' : 'Creando...') : 
                          (editingTeacher ? 'Actualizar Profesor' : 'Crear Profesor')
                        }
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <Card className="modern-card">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th>Nombre Completo</th>
                        <th>Grados Asignados</th>
                        <th>Salón hogar</th>
                        <th>Materias</th>
                        <th>Email</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {teachers.map((teacher) => (
                        <tr key={teacher.id}>
                          <td>
                            <p className="font-medium text-slate-800">{teacher.full_name}</p>
                          </td>
                          <td>
                            <div className="flex flex-wrap gap-1">
                              {teacher.assigned_grades?.map((grade) => (
                                <Badge key={grade} variant="secondary" className="text-xs">{grade}</Badge>
                              ))}
                            </div>
                          </td>
                          <td><span className="text-sm">{teacher.homeroom_grade || 'Sin asignar'}</span></td>
                          <td>
                            <div className="flex flex-wrap gap-1">
                              {teacher.subjects?.map((subject) => (
                                <Badge key={subject} variant="outline" className="text-xs">{subject}</Badge>
                              ))}
                            </div>
                          </td>
                          <td>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm text-slate-500">{teacher.email || '—'}</span>
                              {teacher.credentials_sent && (
                                <Badge className="bg-emerald-50 text-emerald-700 text-[10px]">Enviadas</Badge>
                              )}
                              <span
                                className={`text-[10px] font-medium ${getLastLoginColor(teacher.last_login_at)}`}
                                title={teacher.last_login_at ? new Date(teacher.last_login_at).toLocaleString('es-PR') : 'No ha iniciado sesión'}
                                data-testid={`teacher-last-login-${teacher.id}`}
                              >
                                · {formatLastLogin(teacher.last_login_at)}
                              </span>
                            </div>
                          </td>
                          <td>
                            <div className="flex space-x-1">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  setSelectedTeacher(teacher);
                                  setShowTeacherDetails(true);
                                }}
                                title="Ver detalles"
                              >
                                <Eye className="w-3 h-3" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => editTeacher(teacher)}
                                title="Editar"
                                className="text-blue-600 hover:text-blue-700"
                              >
                                <Edit className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={async () => {
                                  if (!teacher.email) {
                                    toast.error('El profesor no tiene email configurado');
                                    return;
                                  }
                                  const msg = teacher.credentials_sent
                                    ? `¿Reenviar credenciales a ${teacher.email}? Se generará una nueva contraseña.`
                                    : `¿Enviar credenciales a ${teacher.email}?`;
                                  if (!window.confirm(msg)) return;
                                  try {
                                    const res = await axios.post(`/teachers/${teacher.id}/send-credentials`);
                                    toast.success(res.data.message || 'Credenciales enviadas');
                                    if (res.data.temporary_password) {
                                      alert(`Credenciales generadas:\nUsuario: ${res.data.username}\nContraseña: ${res.data.temporary_password}\n\nCópielas ahora — no se mostrarán de nuevo.`);
                                    }
                                    fetchTeachers();
                                  } catch (err) {
                                    toast.error(err.response?.data?.detail || 'Error al enviar credenciales');
                                  }
                                }}
                                title={teacher.credentials_sent ? 'Reenviar credenciales' : 'Enviar credenciales'}
                                className="text-purple-600 hover:text-purple-700"
                                data-testid={`send-teacher-creds-${teacher.id}`}
                              >
                                <Send className="w-3 h-3" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => deleteTeacher(teacher.id)}
                                title="Eliminar"
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Subjects Tab */}
        {activeTab === 'subjects' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-1 h-6 bg-gradient-to-b from-orange-400 to-orange-600 rounded-full"></div>
                  <h2 className="text-xl font-bold text-slate-800">Gestión de Materias</h2>
                </div>
                <p className="text-sm text-slate-500 ml-5">Administra las materias académicas</p>
              </div>
              <Dialog open={showSubjectDialog} onOpenChange={(open) => {
                setShowSubjectDialog(open);
                if (!open) {
                  setEditingSubject(null);
                  setSubjectForm({
                    name: '',
                    code: '',
                    description: '',
                    credits: '',
                    is_subclass: false,
                    assigned_grades: [],
                    assigned_teacher_id: ''
                  });
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 shadow-sm">
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Materia
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>
                      {editingSubject ? 'Editar Materia' : 'Agregar Nueva Materia'}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubjectSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="subject_name">Nombre de la Materia *</Label>
                        <Input
                          id="subject_name"
                          value={subjectForm.name}
                          onChange={(e) => setSubjectForm({...subjectForm, name: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="subject_code">Código *</Label>
                        <Input
                          id="subject_code"
                          value={subjectForm.code}
                          onChange={(e) => setSubjectForm({...subjectForm, code: e.target.value})}
                          required
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="subject_description">Descripción</Label>
                      <Textarea
                        id="subject_description"
                        value={subjectForm.description}
                        onChange={(e) => setSubjectForm({...subjectForm, description: e.target.value})}
                        rows={3}
                      />
                    </div>
                    
                    <div className="rounded-lg border p-3 space-y-1">
                      <Label htmlFor="subject-is-subclass" className="flex items-center gap-2">
                        <input id="subject-is-subclass" type="checkbox" checked={!!subjectForm.is_subclass}
                          onChange={e => setSubjectForm({...subjectForm, is_subclass: e.target.checked})} />
                        Subclase (no cuenta para nota)
                      </Label>
                      <p className="text-sm text-slate-500">Conserva sus evaluaciones en el registro. Se excluye del promedio, las boletas, las transcripciones y los correos de notas.</p>
                    </div>
                    <div>
                      <Label htmlFor="subject_credits">Créditos</Label>
                      <Input
                        id="subject_credits"
                        type="number"
                        value={subjectForm.credits}
                        onChange={(e) => setSubjectForm({...subjectForm, credits: e.target.value})}
                        min="0"
                      />
                    </div>
                    
                    <div>
                      <Label>Grados Asignados</Label>
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        {grades.map((grade) => (
                          <div key={grade} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`subject-grade-${grade}`}
                              checked={subjectForm.assigned_grades.includes(grade)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSubjectForm({
                                    ...subjectForm, 
                                    assigned_grades: [...subjectForm.assigned_grades, grade]
                                  });
                                } else {
                                  setSubjectForm({
                                    ...subjectForm, 
                                    assigned_grades: subjectForm.assigned_grades.filter(g => g !== grade)
                                  });
                                }
                              }}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <Label htmlFor={`subject-grade-${grade}`} className="text-sm">{grade}</Label>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="assigned_teacher">Profesor Asignado</Label>
                      <Select 
                        value={subjectForm.assigned_teacher_id} 
                        onValueChange={(value) => setSubjectForm({...subjectForm, assigned_teacher_id: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar profesor" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">Sin asignar</SelectItem>
                          {teachers.map((teacher) => (
                            <SelectItem key={teacher.id} value={teacher.id}>
                              {teacher.full_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="flex justify-end space-x-4">
                      <Button type="button" variant="outline" onClick={() => setShowSubjectDialog(false)}>
                        Cancelar
                      </Button>
                      <Button type="submit" disabled={loading}>
                        {loading ? 
                          (editingSubject ? 'Actualizando...' : 'Creando...') : 
                          (editingSubject ? 'Actualizar Materia' : 'Crear Materia')
                        }
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <Card className="modern-card">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th>Código</th>
                        <th>Nombre</th>
                        <th>Grados Asignados</th>
                        <th>Profesor Asignado</th>
                        <th>Créditos</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subjects.map((subject) => (
                        <tr key={subject.id}>
                          <td>
                            <span className="font-mono text-sm bg-slate-100 px-2 py-1 rounded-lg">{subject.code}</span>
                          </td>
                          <td>
                            <p className="font-medium text-slate-800">{subject.name}</p>
                            {subject.is_subclass && <Badge variant="secondary">Subclase · No cuenta para nota</Badge>}
                            {subject.description && (
                              <p className="text-sm text-slate-400">{subject.description}</p>
                            )}
                          </td>
                          <td>
                            <div className="flex flex-wrap gap-1">
                              {subject.assigned_grades?.map((grade) => (
                                <Badge key={grade} variant="secondary" className="text-xs">{grade}</Badge>
                              ))}
                            </div>
                          </td>
                          <td>
                            {subject.assigned_teacher_id ? (
                              <span className="text-sm text-slate-500">
                                {teachers.find(t => t.id === subject.assigned_teacher_id)?.full_name || 'No asignado'}
                              </span>
                            ) : (
                              <span className="text-sm text-gray-400">Sin asignar</span>
                            )}
                          </td>
                          <td>
                            <span className="text-sm text-slate-500">{subject.credits || '-'}</span>
                          </td>
                          <td>
                            <div className="flex space-x-1">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => editSubject(subject)}
                                title="Editar"
                                className="text-blue-600 hover:text-blue-700"
                              >
                                <Edit className="w-3 h-3" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => deleteSubject(subject.id)}
                                title="Eliminar"
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'reports' && (
          <GradeReportsTab />
        )}


        {/* Income Tab */}
        {activeTab === 'income' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-1 h-6 bg-gradient-to-b from-orange-400 to-orange-600 rounded-full"></div>
                  <h2 className="text-xl font-bold text-slate-800">Gestión de Ingresos</h2>
                </div>
                <p className="text-sm text-slate-500 ml-5">Estadísticas y reporte de ingresos mensuales</p>
              </div>
            </div>

            {/* Month/Year Selector */}
            <Card className="modern-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <Label>Año</Label>
                    <Select value={selectedYear.toString()} onValueChange={(value) => setSelectedYear(parseInt(value))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[2023, 2024, 2025, 2026].map(year => (
                          <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Label>Mes</Label>
                    <Select value={selectedMonth.toString()} onValueChange={(value) => setSelectedMonth(parseInt(value))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[
                          {value: 1, label: 'Enero'}, {value: 2, label: 'Febrero'}, {value: 3, label: 'Marzo'},
                          {value: 4, label: 'Abril'}, {value: 5, label: 'Mayo'}, {value: 6, label: 'Junio'},
                          {value: 7, label: 'Julio'}, {value: 8, label: 'Agosto'}, {value: 9, label: 'Septiembre'},
                          {value: 10, label: 'Octubre'}, {value: 11, label: 'Noviembre'}, {value: 12, label: 'Diciembre'}
                        ].map(month => (
                          <SelectItem key={month.value} value={month.value.toString()}>{month.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stats Cards */}
            {incomeStats && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Card className="modern-card bg-gradient-to-br from-green-500 to-green-600 text-white">
                    <CardHeader>
                      <CardTitle className="flex items-center text-white">
                        <DollarSign className="w-6 h-6 mr-2" />
                        Ingreso Total
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-4xl font-bold">${incomeStats.total_income.toLocaleString('en-US', {minimumFractionDigits: 2})}</p>
                      <p className="text-green-100 mt-2">{incomeStats.total_payments} pagos recibidos</p>
                    </CardContent>
                  </Card>

                  <Card className="modern-card">
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <Calendar className="w-5 h-5 mr-2 text-blue-600" />
                        Período
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold">
                        {['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'][incomeStats.month - 1]} {incomeStats.year}
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="modern-card">
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <TrendingUp className="w-5 h-5 mr-2 text-purple-600" />
                        Promedio por Pago
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold">
                        ${incomeStats.total_payments > 0 ? (incomeStats.total_income / incomeStats.total_payments).toLocaleString('en-US', {minimumFractionDigits: 2}) : '0.00'}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Breakdown by Type */}
                <Card className="modern-card">
                  <CardHeader>
                    <CardTitle>Desglose por Tipo de Pago</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {Object.entries(incomeStats.breakdown).map(([type, data]) => (
                        data.count > 0 && (
                          <div key={type} className="flex items-center justify-between p-4 bg-transparent rounded-lg">
                            <div className="flex items-center gap-3">
                              <CreditCard className="w-5 h-5 text-slate-500" />
                              <div>
                                <p className="font-medium capitalize">{type === 'tuition' ? 'Colegiatura' : type === 'enrollment' ? 'Matrícula' : type === 'books' ? 'Libros' : type === 'uniform' ? 'Uniforme' : type}</p>
                                <p className="text-sm text-slate-500">{data.count} pagos</p>
                              </div>
                            </div>
                            <p className="text-xl font-bold text-green-600">
                              ${data.total.toLocaleString('en-US', {minimumFractionDigits: 2})}
                            </p>
                          </div>
                        )
                      ))}
                      {Object.values(incomeStats.breakdown).every(d => d.count === 0) && (
                        <p className="text-center text-slate-400 py-8">No hay pagos registrados para este período</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Payment List */}
                {incomeStats.payments.length > 0 && (
                  <Card className="modern-card">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle>Lista de Pagos</CardTitle>
                      <Button
                        onClick={exportPaymentsToExcel}
                        className="bg-green-600 hover:bg-green-700"
                        size="sm"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Exportar a Excel
                      </Button>
                    </CardHeader>
                    <CardContent className="p-0">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-transparent">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Fecha</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">ID Estudiante</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Nombre Estudiante</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Tipo</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Monto</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Descripción</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {incomeStats.payments.map((payment) => (
                              <tr key={payment.id} className="hover:bg-transparent">
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                  {new Date(payment.paid_date).toLocaleDateString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">
                                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                                    {payment.student_number || 'N/A'}
                                  </span>
                                </td>
                                <td className="px-6 py-4 text-sm font-medium text-slate-800">
                                  {payment.student_name || 'Desconocido'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <Badge variant="secondary" className="capitalize">
                                    {payment.payment_type === 'tuition' ? 'Colegiatura' : 
                                     payment.payment_type === 'enrollment' ? 'Matrícula' : 
                                     payment.payment_type === 'books' ? 'Libros' : 
                                     payment.payment_type === 'supplies' ? 'Materiales' :
                                     payment.payment_type === 'uniform' ? 'Uniforme' :
                                     payment.payment_type === 'transport' ? 'Transporte' :
                                     payment.payment_type === 'food' ? 'Alimentación' :
                                     payment.payment_type === 'activities' ? 'Actividades' :
                                     payment.payment_type}
                                  </Badge>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap font-medium text-green-600">
                                  ${payment.amount.toLocaleString('en-US', {minimumFractionDigits: 2})}
                                </td>
                                <td className="px-6 py-4 text-sm text-slate-500">
                                  {payment.description || 'Sin descripción'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            {!incomeStats && (
              <Card className="modern-card">
                <CardContent className="p-12 text-center">
                  <p className="text-slate-400">Cargando estadísticas...</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}


        {/* Announcements Tab */}
        {activeTab === 'announcements' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-1 h-6 bg-gradient-to-b from-orange-400 to-orange-600 rounded-full"></div>
                  <h2 className="text-xl font-bold text-slate-800">Anuncios y Circulares</h2>
                </div>
                <p className="text-sm text-slate-500 ml-5">Enviar comunicados a todos los padres por email</p>
              </div>
              <Button onClick={() => setShowAnnouncementDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Anuncio
              </Button>
            </div>

            {/* Announcements List */}
            <div className="space-y-4">
              {announcements.length > 0 ? announcements.map((announcement) => (
                <Card key={announcement.id} className="modern-card">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="flex items-center gap-2">
                          <Bell className="w-5 h-5 text-blue-600" />
                          {announcement.title}
                        </CardTitle>
                        <p className="text-sm text-slate-400 mt-1">
                          {new Date(announcement.created_at).toLocaleString()} 
                          {announcement.sent_at && ` • Enviado a ${announcement.recipient_count} padres`}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteAnnouncement(announcement.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 whitespace-pre-wrap">{announcement.content}</p>
                    
                    {announcement.attachment_name && (
                      <div className="mt-4 p-3 bg-blue-50 rounded-lg flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-medium">📎 Archivo adjunto: {announcement.attachment_name}</span>
                        </div>
                        <a
                          href={`${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/announcements/download/${announcement.attachment_url}`}
                          download
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-800 underline"
                        >
                          Descargar
                        </a>
                      </div>
                    )}
                    
                    {announcement.attachment_mention && (
                      <div className="mt-3 p-3 bg-transparent rounded-lg flex items-center gap-2">
                        <FileText className="w-4 h-4 text-slate-500" />
                        <span className="text-sm font-medium">📝 Documento mencionado: {announcement.attachment_mention}</span>
                      </div>
                    )}
                    
                    {announcement.target_grades && announcement.target_grades.length > 0 && (
                      <div className="mt-3 flex items-center gap-2">
                        <span className="text-xs text-slate-400">Dirigido a:</span>
                        <div className="flex flex-wrap gap-1">
                          {announcement.target_grades.map((grade) => (
                            <Badge key={grade} variant="secondary" className="text-xs">{grade}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )) : (
                <Card className="modern-card">
                  <CardContent className="p-12 text-center text-slate-400">
                    <Bell className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No hay anuncios publicados</p>
                    <p className="text-sm mt-2">Crea tu primer anuncio para comunicarte con los padres</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <SettingsTab 
            settings={settings} 
            updateSettings={updateSettings}
            promoteStudents={promoteStudents}
            settingsLoading={settingsLoading}
          />
        )}
        
        {activeTab === 'hr' && <HumanResourcesTab />}
        {activeTab === 'clockinout' && <ClockInOutTab />}
        {activeTab === 'clockhistory' && <ClockHistoryTab />}
        {activeTab === 'leave-requests' && <LeaveRequestsTab role="admin" />}
        {activeTab === 'myprofile' && <MyProfileTab />}
        {activeTab === 'planning' && <WeeklyPlanningTab role="admin" />}
        {activeTab === 'payroll' && <PayrollTab />}
        {activeTab === 'invoices' && <InvoicesTab />}
        {activeTab === 'cashflow' && <CashFlowTab />}
        {activeTab === 'calendars' && <CalendarsTab />}
        {activeTab === 'packages' && <PackagesTab />}

        {/* Forms Tab */}
        {activeTab === 'forms' && <FormsTab />}
        {activeTab === 'historical' && <HistoricalGradesTab />}
        {activeTab === 'academic-progress' && <AcademicProgressTab />}
        {activeTab === 'admin-attendance' && <AdminAttendanceTab />}
      </div>

      {/* Dialogs */}
      <StudentDetailsDialog 
        student={selectedStudent}
        open={showStudentDetails}
        onClose={() => {
          setShowStudentDetails(false);
          setSelectedStudent(null);
        }}
        onUpdate={() => {
          fetchStudents();
          fetchDashboardStats();
        }}
      />

      <TeacherDetailsDialog 
        teacher={selectedTeacher}
        open={showTeacherDetails}
        onClose={() => {
          setShowTeacherDetails(false);
          setSelectedTeacher(null);
        }}
        onUpdate={() => {
          fetchTeachers();
          fetchDashboardStats();
        }}
      />

      {/* Announcement Dialog */}
      <Dialog open={showAnnouncementDialog} onOpenChange={setShowAnnouncementDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuevo Anuncio</DialogTitle>
            <DialogDescription>
              Este anuncio será enviado por email a los padres
              {announcementForm.target_grades.length > 0 
                ? ` de los grados: ${announcementForm.target_grades.join(', ')}` 
                : ' de todos los grados'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAnnouncementSubmit} className="space-y-4">
            <div>
              <Label htmlFor="title">Título del Anuncio *</Label>
              <Input
                id="title"
                value={announcementForm.title}
                onChange={(e) => setAnnouncementForm({...announcementForm, title: e.target.value})}
                placeholder="Ej: Reunión de Padres, Evento Escolar..."
                required
              />
            </div>

            <div>
              <Label htmlFor="content">Mensaje *</Label>
              <textarea
                id="content"
                value={announcementForm.content}
                onChange={(e) => setAnnouncementForm({...announcementForm, content: e.target.value})}
                placeholder="Escribe el contenido del anuncio..."
                required
                rows={8}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <Label>Grados Destinatarios</Label>
              <p className="text-xs text-slate-400 mb-2">Selecciona los grados a los que se enviará el anuncio. Si no seleccionas ninguno, se enviará a todos.</p>
              <div className="grid grid-cols-3 gap-2 p-3 border border-gray-200 rounded-md">
                {grades.map((grade) => (
                  <div key={grade} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`announcement-grade-${grade}`}
                      checked={announcementForm.target_grades.includes(grade)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setAnnouncementForm({
                            ...announcementForm, 
                            target_grades: [...announcementForm.target_grades, grade]
                          });
                        } else {
                          setAnnouncementForm({
                            ...announcementForm, 
                            target_grades: announcementForm.target_grades.filter(g => g !== grade)
                          });
                        }
                      }}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <Label htmlFor={`announcement-grade-${grade}`} className="text-sm cursor-pointer">{grade}</Label>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="attachment">Adjuntar Archivo (opcional)</Label>
              <Input
                id="attachment"
                type="file"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (file) {
                    try {
                      const formData = new FormData();
                      formData.append('file', file);
                      
                      const response = await axios.post('/announcements/upload', formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                      });
                      
                      setAnnouncementForm({
                        ...announcementForm,
                        attachment_url: response.data.file_url,
                        attachment_name: response.data.file_name
                      });
                      
                      toast.success('Archivo subido exitosamente');
                    } catch (error) {
                      console.error('Error uploading file:', error);
                      toast.error('Error al subir archivo');
                    }
                  }
                }}
                className="cursor-pointer"
              />
              {announcementForm.attachment_name && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  Archivo adjunto: {announcementForm.attachment_name}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="attachment_mention">O Mencionar Documento (opcional)</Label>
              <Input
                id="attachment_mention"
                type="text"
                value={announcementForm.attachment_mention}
                onChange={(e) => setAnnouncementForm({...announcementForm, attachment_mention: e.target.value})}
                placeholder="Ej: Circular N°5, Formulario de Inscripción..."
              />
              <p className="text-xs text-slate-400 mt-1">
                Puedes subir un archivo o mencionar un documento, o ambos.
              </p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-yellow-800">Importante:</p>
                  <p className="text-yellow-700">
                    Este anuncio será enviado inmediatamente por email. 
                    Verifica que el servidor SMTP esté configurado en Configuración.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-4 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowAnnouncementDialog(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                <Send className="w-4 h-4 mr-2" />
                {loading ? 'Enviando...' : 'Enviar Anuncio'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
